import { useMemo, useState } from "react";

import { US_STATE_OPTIONS } from "./careerIntentCatalog";

export default function CareerIntentStatePicker({
  disabled = false,
  onChange,
  value
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  value: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const atMaxSelections = value.length >= 3;

  const visibleStates = useMemo(
    () =>
      US_STATE_OPTIONS.filter(
        (option) =>
          !normalizedSearch ||
          option.label.toLowerCase().includes(normalizedSearch) ||
          option.value.toLowerCase().includes(normalizedSearch)
      ),
    [normalizedSearch]
  );

  const toggleState = (stateCode: string) => {
    if (disabled) return;
    if (value.includes(stateCode)) {
      onChange(value.filter((selected) => selected !== stateCode));
      return;
    }
    if (atMaxSelections) return;
    onChange([...value, stateCode]);
  };

  return (
    <div>
      <p className="mb-4 text-body-small text-sys-brand-on-surface-variant">
        Select up to 3. Leave blank if no preference.
      </p>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-label="Choose states"
        className="flex min-h-12 w-full items-center justify-between rounded-lg border border-sys-brand-outline bg-sys-brand-surface px-4 py-3 text-left"
        data-testid="career-intent-state-picker"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="flex-1 text-body-large text-sys-brand-on-surface">
          {value.length > 0 ? `${value.length} state${value.length === 1 ? "" : "s"} selected` : "Select states…"}
        </span>
        <span className="text-title-medium text-sys-brand-on-surface-variant">{isOpen ? "−" : "+"}</span>
      </button>

      {value.length > 0 ? (
        <div className="mt-3 flex flex-row flex-wrap">
          {value.map((stateCode) => {
            const state = US_STATE_OPTIONS.find((option) => option.value === stateCode);
            if (!state) return null;

            return (
              <button
                type="button"
                aria-checked={true}
                aria-label={`Remove ${state.label}`}
                className="mb-2 mr-2 rounded-full border border-sys-brand-primary bg-sys-brand-primary/5 px-3 py-2 text-label-large text-sys-brand-primary"
                data-testid={`career-intent-state-chip-${state.value}`}
                disabled={disabled}
                key={state.value}
                onClick={() => toggleState(state.value)}
                role="checkbox"
              >
                {state.label} ×
              </button>
            );
          })}
        </div>
      ) : null}

      {isOpen ? (
        <div className="mt-3 rounded-lg border border-sys-brand-outline bg-sys-brand-surface p-3">
          <div className="mb-3 flex flex-row justify-end">
            <button
              type="button"
              aria-label="Close state picker"
              className="rounded-md px-2 py-1 text-label-large text-sys-brand-primary"
              data-testid="career-intent-state-picker-close"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
          <input
            aria-label="Search states"
            className="mb-3 min-h-11 w-full rounded-md border border-sys-brand-outline bg-sys-brand-surface px-3 py-2 text-body-large text-sys-brand-on-surface"
            data-testid="career-intent-state-search"
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search states"
            value={search}
          />
          <div role="list">
            {visibleStates.map((state) => {
              const selected = value.includes(state.value);
              const optionDisabled = disabled || (!selected && atMaxSelections);

              return (
                <button
                  type="button"
                  aria-checked={selected}
                  className={`mb-1 flex min-h-11 w-full items-center rounded-md px-3 py-2 text-left ${
                    selected ? "bg-sys-brand-primary/5" : ""
                  }${optionDisabled ? " opacity-50" : ""}`}
                  data-testid={`career-intent-state-option-${state.value}`}
                  disabled={optionDisabled}
                  key={state.value}
                  onClick={() => toggleState(state.value)}
                  role="checkbox"
                >
                  <span className="mr-3 w-6 text-center text-title-medium text-sys-brand-primary">
                    {selected ? "✓" : ""}
                  </span>
                  <span className="text-body-large text-sys-brand-on-surface">{state.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
